import {
  type PlayerItemResponse,
  rdbV2OrganizationsTransfersDetail,
  type RdbV2OrganizationsTransfersDetailParams,
} from '@on3/api';
import type {
  ErrorData,
  ICommitsIndexProps,
} from '@on3/ui-lib/api/schema/custom-contracts';
import { Toggle } from '@on3/ui-lib/components/Toggle/Toggle';
import { externalApi, useSite, useToggle } from '@on3/ui-lib/index';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { type AxiosError } from 'axios';
import { InterestsNav } from 'components/College/Interests/Nav';
import { IndustryComparisonListAlt } from 'components/Db/Rankings/IndustryComparison/IndustryComparisonListAlt';
import { AdSidebarLayout } from 'components/Layouts/AdSidebarLayout';
import { useRouter } from 'next/router';
import { type FC, useCallback, useMemo, useState } from 'react';

import { useSortList } from '../Commits/CommitList';
import styles from '../Commits/CommitList.module.scss';
import { CommitListItem } from '../Commits/CommitListItem';
import { CommitColumnHeaders } from '../Commits/Header';
import { InterestLegend } from '../General/InterestLegend';
import { InterestListFilters } from '../General/InterestListFilters';

export const TransfersList: FC<ICommitsIndexProps> = ({
  playerList,
  teams,
  years,
  title,
  toggled = false,
}) => {
  const router = useRouter();
  const { currentSite, sportsData } = useSite();
  const { sport, year, page } = router.query;
  const defaultPage = parseInt(`${page}`, 10);
  const orgKey = currentSite.organizationKey;
  const pagination = playerList?.pagination;
  const isTeam = router.query.college;
  const isIndustryComparison = router.asPath.includes(
    'industry-comparison-transfers',
  );
  const [list, setList] = useState(playerList?.list);
  const [pageNumber, setPageNumber] = useState(defaultPage || 1);
  const [disable, setDisable] = useState(false);
  const [selected, setSelected] = useState('status');
  const [ascending, setAscending] = useState(false);
  const [toggle, setToggle] = useToggle(toggled);
  const [transferType, setTransferType] = useState('All');
  const isIndustrySport =
    sportsData?.find((sportData) => slugify(sportData.name) === sport)
      ?.isIndustryRankable || false;
  const sortList = useSortList(list, 'transfers');
  const handleToggle = useCallback(() => {
    if (isTeam) {
      const newPath = isIndustryComparison
        ? '/college/[college]/[sport]/[year]/transfers/'
        : '/college/[college]/[sport]/[year]/industry-comparison-transfers/';

      router.push({
        pathname: newPath,
        query: { ...router.query },
      });
    }

    setToggle((prevState: boolean) => !prevState);
  }, [isIndustryComparison, isTeam, router, setToggle]);

  const commitCount = useMemo(
    () =>
      playerList?.list?.filter((item) => !item.status?.transferEntered).length,
    [playerList?.list],
  );
  const transferCount = useMemo(
    () =>
      playerList?.list?.filter((item) => item.status?.transferEntered).length,
    [playerList?.list],
  );

  const showLoadMoreButton =
    pagination?.count &&
    pagination?.count > 50 &&
    (pagination?.pageCount ?? 0) > pageNumber &&
    list &&
    list?.length < pagination?.count;

  const handleClick: React.MouseEventHandler = useCallback(async () => {
    setDisable(true);
    if (!orgKey || !year) return;
    const newPageNumber = pageNumber + 1;
    const sportKey = sportsData?.find(
      (sp) => sp?.name && slugify(sp?.name) === sport,
    )?.key;
    const params: RdbV2OrganizationsTransfersDetailParams = {
      sportKey,
      orgKey,
      year: +year,
      page: newPageNumber,
    };

    try {
      const response = await rdbV2OrganizationsTransfersDetail(
        externalApi,
        params,
      );
      const currentPage = response.pagination?.currentPage ?? 0;

      if (currentPage) {
        router.replace(
          {
            query: { ...router.query, page: currentPage },
          },
          undefined,
          { shallow: true },
        );
        list &&
          setList([
            ...list,
            ...((response?.list || []) as PlayerItemResponse[]),
          ]);

        setPageNumber(currentPage);
      }
    } catch (e: unknown) {
      const { response, message }: AxiosError<ErrorData> =
        e as AxiosError<ErrorData>;

      console.error(
        'Latest Commits Error: ',
        response?.data?.message || message,
      );
    } finally {
      setDisable(false);
    }
  }, [list, orgKey, pageNumber, router, sport, sportsData, year]);

  const handleChange: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      const { dataset } = e.currentTarget;

      const { sorttype = '', flip = 'false' } = dataset;
      //  flips order of the list
      const isFlipped = flip === 'true';
      let orderBy = ascending;

      if (sorttype !== selected) {
        setSelected(sorttype);
        orderBy = isFlipped;
        setAscending(isFlipped);
      } else {
        orderBy = !ascending;
        setAscending((prev) => !prev);
      }

      const sortedList = sortList(sorttype, orderBy);

      sortedList && setList(sortedList);
    },
    [ascending, selected, sortList],
  );
  const handleTransferType = useCallback(
    (type: string) => () => {
      setTransferType(type);
      setSelected('status');
      setAscending(false);
      if (type === 'In') {
        const transfersIn = playerList?.list?.filter(
          (item) =>
            item.status?.committedAsset?.key === currentSite?.organizationKey,
        );

        transfersIn && setList([...transfersIn]);
      }

      if (type === 'Out') {
        const transfersOut = playerList?.list?.filter(
          (item) =>
            item.status?.transferredAsset?.key &&
            item.status.transferredAsset.key === currentSite?.organizationKey &&
            item.status.type?.toLowerCase() !== 'withdrawn',
        );

        transfersOut && setList([...transfersOut]);
      }

      if (type === 'All') {
        setList(playerList?.list);
      }
    },
    [playerList?.list, currentSite, setTransferType],
  );

  return (
    <AdSidebarLayout>
      <section className={styles.block}>
        <header className={styles.header}>
          <h1>{title}</h1>

          <div className={styles.filterWrapper}>
            <InterestListFilters teams={teams} years={years} />
            {isIndustrySport && (
              <Toggle
                checked={toggle}
                id="industry-comparison-toggle"
                label="Industry Comparison"
                onChange={handleToggle}
              />
            )}
          </div>
        </header>
        <div className={styles.container}>
          <InterestsNav />
          {!!list?.length && (
            <div className={styles.transferModules}>
              {(!!commitCount || !!transferCount) && (
                <div className={styles.transferCounts}>
                  {!!commitCount && (
                    <dl>
                      <dt>Commits</dt>
                      <dd>{commitCount}</dd>
                    </dl>
                  )}
                  {!!transferCount && (
                    <dl>
                      <dt>Transfers</dt>
                      <dd>{transferCount}</dd>
                    </dl>
                  )}
                </div>
              )}

              <div className={styles.transferFiltersContainer}>
                <button
                  data-active={transferType === 'All'}
                  onClick={handleTransferType('All')}
                >
                  All
                </button>{' '}
                <button
                  data-active={transferType === 'In'}
                  onClick={handleTransferType('In')}
                >
                  Transfers In
                </button>
                <button
                  data-active={transferType === 'Out'}
                  onClick={handleTransferType('Out')}
                >
                  Transfers Out
                </button>
              </div>
            </div>
          )}
        </div>

        {toggle ? (
          <IndustryComparisonListAlt key={transferType} playerList={list} />
        ) : (
          <div className={styles.listWrapper}>
            {list?.length ? (
              <>
                <CommitColumnHeaders
                  ascending={ascending}
                  onChange={handleChange}
                  selected={selected}
                />
                <ul>
                  {list.map((item, index) => (
                    <li
                      key={`${item.psoKey}-${index}-${selected}-${ascending}-${transferType}`}
                    >
                      <CommitListItem data={item} />
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className={styles.noListText}>
                There are no players for this selection.
              </p>
            )}
          </div>
        )}

        {!!showLoadMoreButton && (
          <button
            className={styles.btnLoadMore}
            disabled={disable}
            onClick={handleClick}
          >
            Load More
          </button>
        )}
        {!!list?.length && <InterestLegend className={styles.legend} />}
      </section>
    </AdSidebarLayout>
  );
};
